/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Assessment as AssessmentIcon,
  AutoAwesome as AutoAwesomeIcon,
  BarChart as BarChartIcon,
  Code as CodeIcon,
  DashboardCustomize as DashboardCustomizeIcon,
  FolderShared as FolderSharedIcon,
  Insights as InsightsIcon,
  Memory as MemoryIcon,
  PrecisionManufacturing as PrecisionManufacturingIcon,
  Psychology as PsychologyIcon,
  SettingsSuggest as SettingsSuggestIcon,
  SmartToy as SmartToyIcon,
  Storage as StorageIcon,
  Tune as TuneIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { listBots } from '../../../api/botsApi';
import { aiSystemApi, executionApi } from '../../services/api';

type HubItem = {
  label: string;
  path: string;
  description: string;
  icon: React.ReactNode;
  summary?: string;
};

type HubSection = {
  title: string;
  description: string;
  accent: string;
  icon: React.ReactNode;
  items: HubItem[];
};

type HeroStat = {
  label: string;
  value: string;
  helper: string;
};

type AutomationHubMetrics = {
  systemsTotal: number;
  systemsActive: number;
  botsTotal: number;
  botsActive: number;
  runs24h: number;
  runs7d: number;
  successRate: number;
};

const numberFormatter = new Intl.NumberFormat();

const formatWholeNumber = (value: number) => numberFormatter.format(Math.max(0, Math.round(value)));

const getSectionItemSize = (itemCount: number) => {
  if (itemCount <= 1) {
    return { xs: 12 };
  }

  if (itemCount === 2) {
    return { xs: 12, md: 6 };
  }

  if (itemCount === 3) {
    return { xs: 12, md: 4 };
  }

  return { xs: 12, md: 6 };
};

const normalizeBots = (response: any) => {
  const payload = response?.data ?? response;

  if (Array.isArray(payload)) {
    return {
      total: payload.length,
      results: payload,
    };
  }

  const results = Array.isArray(payload?.results) ? payload.results : [];

  return {
    total: typeof payload?.count === 'number' ? payload.count : results.length,
    results,
  };
};

const AIHubPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const {
    data: metrics,
    isLoading: metricsLoading,
    isFetching: metricsFetching,
    isError: metricsError,
  } = useQuery({
    queryKey: ['automation-hub-metrics'],
    queryFn: async (): Promise<AutomationHubMetrics> => {
      const [systemsData, activeSystemsData, executionStats, botResponse] = await Promise.all([
        aiSystemApi.list({ page_size: 1 }),
        aiSystemApi.list({ page_size: 1, is_active: true }),
        executionApi.stats(),
        listBots({ params: { page_size: 500 } }),
      ]);

      const bots = normalizeBots(botResponse);
      const activeBots = bots.results.filter((bot) => Boolean(bot?.is_active)).length;

      return {
        systemsTotal: systemsData.count ?? systemsData.results.length,
        systemsActive: activeSystemsData.count ?? activeSystemsData.results.length,
        botsTotal: bots.total,
        botsActive: activeBots,
        runs24h: executionStats.recent?.last_24h ?? 0,
        runs7d: executionStats.recent?.last_7d ?? 0,
        successRate: executionStats.success_rate ?? 0,
      };
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const isAdmin = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.is_staff === true;
      }
    } catch {
      // Ignore malformed user payloads.
    }
    return false;
  }, []);

  const heroStats = useMemo<HeroStat[]>(() => {
    if (!metrics) {
      return [
        {
          label: 'Active Systems',
          value: '—',
          helper: metricsLoading ? 'Refreshing system count' : 'Live count unavailable',
        },
        {
          label: 'Live Bots',
          value: '—',
          helper: metricsLoading ? 'Refreshing bot count' : 'Live count unavailable',
        },
        {
          label: 'Runs Last 24h',
          value: '—',
          helper: metricsLoading ? 'Refreshing execution count' : 'Live count unavailable',
        },
      ];
    }

    return [
      {
        label: 'Active Systems',
        value: formatWholeNumber(metrics.systemsActive),
        helper: metrics.systemsTotal === 0
          ? 'No systems configured yet'
          : `${formatWholeNumber(metrics.systemsTotal)} total configured`,
      },
      {
        label: 'Live Bots',
        value: formatWholeNumber(metrics.botsActive),
        helper: metrics.botsTotal === 0
          ? 'No bots configured yet'
          : `${formatWholeNumber(metrics.botsTotal)} bots available`,
      },
      {
        label: 'Runs Last 24h',
        value: formatWholeNumber(metrics.runs24h),
        helper: metrics.runs7d === 0
          ? 'No recent run history yet'
          : `${formatWholeNumber(metrics.runs7d)} runs in the last 7 days`,
      },
    ];
  }, [metrics, metricsLoading]);

  const sections = useMemo<HubSection[]>(() => {
    const items: HubSection[] = [
      {
        title: 'Automate',
        description: 'Create, adjust, and activate the automation assets you actively change.',
        accent: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.18)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 100%)`,
        icon: <AutoAwesomeIcon color="primary" />,
        items: [
          {
            label: 'AI Systems',
            path: '/ai/systems',
            description: 'Create, edit, and manage end-to-end AI workflows.',
            icon: <SmartToyIcon color="primary" fontSize="small" />,
            summary: metrics
              ? metrics.systemsTotal === 0
                ? 'No systems configured yet'
                : `${formatWholeNumber(metrics.systemsActive)} active of ${formatWholeNumber(metrics.systemsTotal)} total`
              : undefined,
          },
          {
            label: 'Trading Bots',
            path: '/bots',
            description: 'Manage no-code trading automations and live bot versions.',
            icon: <PrecisionManufacturingIcon color="primary" fontSize="small" />,
            summary: metrics
              ? metrics.botsTotal === 0
                ? 'No bots configured yet'
                : `${formatWholeNumber(metrics.botsActive)} live of ${formatWholeNumber(metrics.botsTotal)} total`
              : undefined,
          },
        ],
      },
      {
        title: 'Monitor',
        description: 'Start from run history, reliability, or cost when you need operational evidence fast.',
        accent: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.18)} 0%, ${alpha(theme.palette.info.main, 0.04)} 100%)`,
        icon: <InsightsIcon color="info" />,
        items: [
          {
            label: 'Runs',
            path: '/ai/analytics?tab=runs',
            description: 'Inspect recent runs and jump into the automation history that needs review.',
            icon: <BarChartIcon color="info" fontSize="small" />,
            summary: metrics
              ? metrics.runs24h === 0
                ? 'No runs in the last 24 hours'
                : `${formatWholeNumber(metrics.runs24h)} runs in the last 24 hours`
              : undefined,
          },
          {
            label: 'Reliability',
            path: '/ai/analytics?tab=reliability',
            description: 'Jump straight to failure trends and health signals for recent automation runs.',
            icon: <AssessmentIcon color="info" fontSize="small" />,
            summary: metrics
              ? metrics.runs7d === 0
                ? 'No recent run history yet'
                : `${Math.round(metrics.successRate)}% success across ${formatWholeNumber(metrics.runs7d)} recent runs`
              : undefined,
          },
          {
            label: 'Cost',
            path: '/ai/analytics?tab=cost',
            description: 'Review automation usage patterns and cost trends from the same analytics workspace.',
            icon: <BarChartIcon color="info" fontSize="small" />,
            summary: metrics
              ? metrics.runs7d === 0
                ? 'Usage data will appear after recent runs'
                : `${formatWholeNumber(metrics.runs7d)} recent runs feeding usage trends`
              : undefined,
          },
        ],
      },
      {
        title: 'Configure',
        description: 'Manage the reusable building blocks that shape how automation behaves.',
        accent: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.18)} 0%, ${alpha(theme.palette.warning.main, 0.04)} 100%)`,
        icon: <TuneIcon color="warning" />,
        items: [
          {
            label: 'Agents',
            path: '/ai/agents',
            description: 'Configure reusable AI agents and builders.',
            icon: <PsychologyIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Prompt Snippets',
            path: '/ai/snippets',
            description: 'Reuse prompt fragments and structured prompt patterns.',
            icon: <CodeIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Indicator Presets',
            path: '/ai/indicator-sets',
            description: 'Maintain reusable indicator bundles for systems and experts.',
            icon: <SettingsSuggestIcon color="warning" fontSize="small" />,
          },
          {
            label: 'Global Storage',
            path: '/ai/global-storage',
            description: 'Keep shared files, text, and assets available across systems.',
            icon: <StorageIcon color="warning" fontSize="small" />,
          },
        ],
      },
      {
        title: 'Advanced',
        description: 'Inspect lower-level system components and reusable expert building blocks.',
        accent: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.18)} 0%, ${alpha(theme.palette.success.main, 0.04)} 100%)`,
        icon: <DashboardCustomizeIcon color="success" />,
        items: [
          {
            label: 'Expert Library',
            path: '/ai/experts',
            description: 'Audit experts that already exist inside AI systems.',
            icon: <FolderSharedIcon color="success" fontSize="small" />,
          },
        ],
      },
    ];

    if (isAdmin) {
      items.push({
        title: 'Admin',
        description: 'Access model-lab and platform-level configuration surfaces.',
        accent: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.18)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        icon: <MemoryIcon color="secondary" />,
        items: [
          {
            label: 'Model Lab',
            path: '/ai/ml',
            description: 'Work with datasets, features, models, training, and deployments.',
            icon: <MemoryIcon color="secondary" fontSize="small" />,
          },
          {
            label: 'LLM Providers',
            path: '/ai/providers',
            description: 'Configure provider credentials and availability.',
            icon: <StorageIcon color="secondary" fontSize="small" />,
          },
          {
            label: 'LLM Models',
            path: '/ai/models',
            description: 'Manage model inventory and routing options.',
            icon: <TuneIcon color="secondary" fontSize="small" />,
          },
          {
            label: 'Service Instructions',
            path: '/ai/service-instructions',
            description: 'Maintain shared service-level instruction sets.',
            icon: <CodeIcon color="secondary" fontSize="small" />,
          },
        ],
      });
    }

    return items;
  }, [
    isAdmin,
    metrics,
    theme.palette.info.main,
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2, sm: 3 },
        width: '100%',
        maxWidth: '1280px',
        mx: 'auto',
        my: { xs: 1, sm: 2, md: 3 },
      }}
    >
      <Card
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 48%, ${alpha(theme.palette.info.main, 0.08)} 100%)`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <Stack spacing={2.25}>
                <Box>
                  <Typography variant="overline" color="primary.main" sx={{ letterSpacing: 1.6, fontWeight: 700 }}>
                    Automation
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, fontSize: { xs: '1.8rem', sm: '2.2rem' } }}>
                    Start from the job you need to finish.
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 1.5, maxWidth: 720 }}>
                    Create automation, monitor recent runs, and tune reusable components without digging through nested menus.
                  </Typography>
                </Box>

                <Grid container spacing={1.25}>
                  {heroStats.map((stat) => (
                    <Grid key={stat.label} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          height: '100%',
                          bgcolor: alpha(theme.palette.background.paper, 0.82),
                        }}
                      >
                        <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1.1 }}>
                          {stat.label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {stat.helper}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, lg: 5 }}>
              <Paper
                variant="outlined"
                sx={{
                  height: '100%',
                  p: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.82),
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Choose a lane
                </Typography>
                <Stack spacing={1.25}>
                  <Typography variant="body2" color="text.secondary">
                    Start in <strong>Automate</strong> when you are creating or activating automation.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start in <strong>Monitor</strong> when you need to inspect runs, review reliability, or check cost patterns.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start in <strong>Configure</strong> when you are updating agents, prompts, presets, or shared assets.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Keep an eye on the live counts to see whether work is piling up before you drill into a page.
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {metricsFetching ? <CircularProgress size={14} /> : null}
                      <Typography variant="caption" color="text.secondary">
                        {metricsError
                          ? 'Live counts are temporarily unavailable. Navigation still works normally.'
                          : metrics
                          ? metrics.runs7d === 0
                            ? 'Live counts refresh every 30 seconds. Reliability data will appear after the first recent runs.'
                            : `Live counts refresh every 30 seconds. Current reliability is ${Math.round(metrics.successRate)}% across recent runs.`
                          : 'Loading live counts for systems, bots, and runs.'}
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {metricsError && (
        <Alert severity="info">
          Live counts could not be refreshed, but the Automation Hub navigation remains fully available.
        </Alert>
      )}

      <Grid container spacing={{ xs: 2, sm: 2.5 }}>
        {sections.map((section) => (
          <Grid key={section.title} size={{ xs: 12 }}>
            <Card sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ p: 2.25, borderBottom: '1px solid', borderColor: 'divider', background: section.accent }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box sx={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 2.5, bgcolor: alpha(theme.palette.background.paper, 0.9), border: '1px solid', borderColor: 'divider' }}>
                      {section.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                        {section.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Grid container spacing={1.5} sx={{ p: 2 }}>
                  {section.items.map((item) => (
                    <Grid key={item.path} size={getSectionItemSize(section.items.length)}>
                      <Paper
                        component="button"
                        type="button"
                        onClick={() => navigate(item.path)}
                        variant="outlined"
                        sx={{
                          width: '100%',
                          minHeight: 120,
                          p: 1.75,
                          textAlign: 'left',
                          transition: 'transform 120ms ease, border-color 120ms ease, background-color 120ms ease',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover',
                            transform: 'translateY(-1px)',
                          },
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" mb={1.25}>
                          <Box sx={{ display: 'grid', placeItems: 'center', width: 30, height: 30, borderRadius: 2, bgcolor: 'action.hover' }}>
                            {item.icon}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                              {item.label}
                            </Typography>
                            {item.summary && (
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontWeight: 600 }}>
                                {item.summary}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AIHubPage;